import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import PayPalPayment from '@/components/PayPalPayment';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Sparkles, Music, Video, DollarSign, TrendingUp, 
  BarChart, Megaphone, Edit, Loader2, Upload, CheckCircle, Clock
} from 'lucide-react';

const CreatorSpacePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services'); // services, mes-achats, adsense
  const [services, setServices] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  
  // Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('ORANGE_MONEY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paypalApprovalUrl, setPaypalApprovalUrl] = useState(null);
  
  // AdSense Form
  const [showAdSenseForm, setShowAdSenseForm] = useState(false);
  const [adSenseForm, setAdSenseForm] = useState({
    full_name: '',
    address: '',
    country: ''
  });
  const [passportFile, setPassportFile] = useState(null);
  const [submittingAdSense, setSubmittingAdSense] = useState(false);
  const [myAdSenseApp, setMyAdSenseApp] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servicesRes, purchasesRes, adSenseRes] = await Promise.all([
        api.get('/creator-space/services'),
        api.get('/creator-space/my-purchases'),
        api.get('/creator-space/adsense/my-application')
      ]);
      
      setServices(servicesRes.data.services || []);
      setMyPurchases(purchasesRes.data.purchases || []);
      setMyAdSenseApp(adSenseRes.data.application || null);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseService = (service) => {
    setSelectedService(service);
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!selectedService) return;
    
    if (paymentMethod === 'PAYPAL') {
      // PayPal flow
      setPaymentLoading(true);
      try {
        const response = await api.post('/creator-space/purchase', {
          service_type: selectedService.type,
          payment_method: paymentMethod
        });
        
        if (response.data.approval_url) {
          setPaypalApprovalUrl(response.data.approval_url);
          toast.success('Redirection vers PayPal...');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur PayPal');
      } finally {
        setPaymentLoading(false);
      }
    } else {
      // BKAPAY (Mobile Money)
      if (!phoneNumber) {
        toast.error('Entrez votre numéro');
        return;
      }
      
      setPaymentLoading(true);
      try {
        await api.post('/creator-space/purchase', {
          service_type: selectedService.type,
          payment_method: paymentMethod,
          phone_number: phoneNumber
        });
        
        toast.success('🎉 Paiement initié ! Confirmez sur votre téléphone');
        setShowPaymentModal(false);
        setPhoneNumber('');
        
        // Rafraîchir après 5 secondes
        setTimeout(() => {
          fetchData();
        }, 5000);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur de paiement');
      } finally {
        setPaymentLoading(false);
      }
    }
  };

  const handleAdSenseSubmit = async (e) => {
    e.preventDefault();
    
    if (!passportFile) {
      toast.error('Veuillez uploader votre passeport');
      return;
    }
    
    setSubmittingAdSense(true);
    try {
      const formData = new FormData();
      formData.append('full_name', adSenseForm.full_name);
      formData.append('address', adSenseForm.address);
      formData.append('country', adSenseForm.country);
      formData.append('passport', passportFile);
      
      const response = await api.post('/creator-space/adsense/apply', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('✅ Demande soumise avec succès !');
      setShowAdSenseForm(false);
      setAdSenseForm({ full_name: '', address: '', country: '' });
      setPassportFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmittingAdSense(false);
    }
  };

  const renderMenuTabs = () => {
    const tabs = [
      { id: 'services', icon: Sparkles, label: 'Services' },
      { id: 'mes-achats', icon: CheckCircle, label: 'Mes Achats' },
      { id: 'adsense', icon: DollarSign, label: 'AdSense' }
    ];
    
    return (
      <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-800 pb-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#E50914] text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              <Icon className="inline mr-2" size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderServices = () => (
    <div className="text-center py-12">
      <Card className="glass border-zinc-800">
        <CardContent className="py-12">
          <p className="text-zinc-400 text-lg">
            Les services sont maintenant disponibles sur le Dashboard principal
          </p>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="mt-6 bg-[#E50914] hover:bg-[#F8212C]"
          >
            Retour au Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderMyPurchases = () => {
    if (myPurchases.length === 0) {
      return (
        <Card className="glass border-zinc-800">
          <CardContent className="py-12 text-center">
            <p className="text-zinc-400">Aucun achat pour le moment</p>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <div className="space-y-4">
        {myPurchases.map((purchase, index) => (
          <Card key={index} className="glass border-zinc-800">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-white">{purchase.service_label}</h3>
                  <p className="text-sm text-zinc-400">
                    {new Date(purchase.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#F97316]">${purchase.amount_usd}</p>
                  <span className={`text-xs px-2 py-1 rounded ${
                    purchase.payment_status === 'success' 
                      ? 'bg-green-900/30 text-green-400'
                      : purchase.payment_status === 'pending'
                      ? 'bg-yellow-900/30 text-yellow-400'
                      : 'bg-red-900/30 text-red-400'
                  }`}>
                    {purchase.payment_status === 'success' ? '✓ Payé' : 
                     purchase.payment_status === 'pending' ? '⏳ En attente' : '✗ Échoué'}
                  </span>
                </div>
              </div>
              {purchase.fulfilled && (
                <div className="mt-2 text-xs text-green-400">
                  ✅ Service livré
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderAdSense = () => {
    if (myAdSenseApp) {
      return (
        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle>Votre demande Google AdSense</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Nom complet</Label>
                <p className="text-white">{myAdSenseApp.full_name}</p>
              </div>
              <div>
                <Label className="text-zinc-400">Pays</Label>
                <p className="text-white">{myAdSenseApp.country}</p>
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Adresse</Label>
                <p className="text-white">{myAdSenseApp.address}</p>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg ${
              myAdSenseApp.status === 'approved' 
                ? 'bg-green-900/30 border border-green-800'
                : myAdSenseApp.status === 'pending'
                ? 'bg-yellow-900/30 border border-yellow-800'
                : 'bg-red-900/30 border border-red-800'
            }`}>
              <p className="font-semibold">
                {myAdSenseApp.status === 'approved' ? '✅ Approuvée' : 
                 myAdSenseApp.status === 'pending' ? '⏳ En cours de révision' : '❌ Rejetée'}
              </p>
              {myAdSenseApp.review_note && (
                <p className="text-sm mt-2">{myAdSenseApp.review_note}</p>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card className="glass border-zinc-800">
        <CardHeader>
          <CardTitle>Validation Google AdSense</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-zinc-400">
            Faites valider votre compte Google AdSense pour monétiser votre contenu YouTube.
            Notre équipe s&apos;occupe de tout !
          </p>
          <div className="bg-zinc-900 rounded-lg p-4 space-y-2 text-sm">
            <p className="text-white">✓ Configuration complète de votre compte AdSense</p>
            <p className="text-white">✓ Vérification d&apos;identité</p>
            <p className="text-white">✓ Support personnalisé</p>
            <p className="text-[#F97316] font-bold mt-3">Prix: $150</p>
          </div>
          <Button 
            onClick={() => setShowAdSenseForm(true)}
            className="w-full bg-[#E50914] hover:bg-[#F8212C]"
          >
            <Upload className="mr-2" size={18} />
            Soumettre une demande
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
            <span className="text-[#E50914]">Espace</span> Créateur
          </h1>
          <p className="text-zinc-400">
            Services professionnels pour promouvoir votre carrière musicale
          </p>
        </div>

        {renderMenuTabs()}

        {activeTab === 'services' && renderServices()}
        {activeTab === 'mes-achats' && renderMyPurchases()}
        {activeTab === 'adsense' && renderAdSense()}
      </main>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="glass border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              {selectedService?.label}
            </DialogTitle>
          </DialogHeader>
          
          {paypalApprovalUrl ? (
            <PayPalPayment approvalUrl={paypalApprovalUrl} />
          ) : (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="text-zinc-400 text-sm">{selectedService?.description}</p>
                <div className="flex justify-between mt-4 font-semibold">
                  <span>Montant</span>
                  <span className="text-[#F97316] text-xl">${selectedService?.price_usd}</span>
                </div>
              </div>

              <div>
                <Label>Méthode de paiement</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                    <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                    <SelectItem value="PAYPAL">PayPal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {paymentMethod !== 'PAYPAL' && (
                <div>
                  <Label>Numéro de téléphone</Label>
                  <Input
                    type="tel"
                    placeholder="+243..."
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
              )}

              <Button
                onClick={handlePaymentSubmit}
                disabled={paymentLoading}
                className="w-full bg-[#E50914] hover:bg-[#F8212C] py-6 text-lg font-semibold"
              >
                {paymentLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  `PAYER $${selectedService?.price_usd}`
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AdSense Form Modal */}
      <Dialog open={showAdSenseForm} onOpenChange={setShowAdSenseForm}>
        <DialogContent className="glass border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Demande de Validation Google AdSense</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAdSenseSubmit} className="space-y-4 py-4">
            <div>
              <Label>Nom complet *</Label>
              <Input
                value={adSenseForm.full_name}
                onChange={(e) => setAdSenseForm({...adSenseForm, full_name: e.target.value})}
                required
                placeholder="Ex: Jean Dupont"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label>Adresse complète *</Label>
              <Input
                value={adSenseForm.address}
                onChange={(e) => setAdSenseForm({...adSenseForm, address: e.target.value})}
                required
                placeholder="Ex: 123 Rue de la Paix, Kinshasa"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label>Pays *</Label>
              <Input
                value={adSenseForm.country}
                onChange={(e) => setAdSenseForm({...adSenseForm, country: e.target.value})}
                required
                placeholder="Ex: RD Congo"
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div>
              <Label>Upload Passeport * (JPG, PNG ou PDF)</Label>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => setPassportFile(e.target.files[0])}
                required
                className="bg-zinc-900 border-zinc-800 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Formats acceptés: JPG, PNG, PDF (max 10MB)
              </p>
            </div>

            <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-3 text-xs text-yellow-200">
              ⚠️ Assurez-vous que votre passeport est valide et lisible
            </div>

            <Button
              type="submit"
              disabled={submittingAdSense}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {submittingAdSense ? <Loader2 className="animate-spin" /> : 'SOUMETTRE LA DEMANDE'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatorSpacePage;
